import React, { useState, useEffect } from 'react';
import '../stylings/BucksBox.css';
import bucksBoxImage from '../imgs/bucksBoxImage.png';
import bucksBoxFrontImage from '../imgs/bucksBoxFrontofStand_IMG.png';
import { Sprout, Calendar, Loader, CheckCircle, AlertCircle } from 'lucide-react';

function BucksBox() {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('weekly');
  const [selectedSize, setSelectedSize] = useState('medium');
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    deliveryNotes: ''
  });
  
  // New states for the waitlist email functionality
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistStatus, setWaitlistStatus] = useState({
    loading: false,
    success: false,
    error: false,
    message: ''
  });
  
  // Animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle waitlist email input changes
  const handleWaitlistEmailChange = (e) => {
    setWaitlistEmail(e.target.value);
    // Reset any previous status messages when user types
    if (waitlistStatus.success || waitlistStatus.error) {
      setWaitlistStatus({
        loading: false,
        success: false,
        error: false,
        message: ''
      });
    }
  };
  
  // Handle plan selection
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
  };
  
  // Handle size selection
  const handleSizeSelect = (size) => {
    setSelectedSize(size);
  };
  
  // Handle adding email to waitlist
  const handleWaitlistSubmit = async () => {
    // Validate email (simple client-side validation)
    if (!waitlistEmail || !waitlistEmail.includes('@') || !waitlistEmail.includes('.')) {
      setWaitlistStatus({
        loading: false,
        success: false,
        error: true,
        message: 'Please enter a valid email address'
      });
      return;
    }
    
    // Set loading state
    setWaitlistStatus({
      loading: true,
      success: false,
      error: false,
      message: 'Adding you to our waitlist...'
    });
    
    try {
      const response = await fetch('https://s9351aaac7.execute-api.us-east-1.amazonaws.com/default/bucks-box-email-wait-list-customer-subscribe-post', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: waitlistEmail })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Success
        setWaitlistStatus({
          loading: false,
          success: true,
          error: false,
          message: data.message || 'Thank you for joining our Buck\'s Box waitlist!'
        });
        // Clear the email input on success
        setWaitlistEmail('');
      } else {
        // API returned an error
        setWaitlistStatus({
          loading: false,
          success: false,
          error: true,
          message: data.message || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      // Network error or other exception
      setWaitlistStatus({
        loading: false,
        success: false,
        error: true,
        message: 'Connection error. Please check your internet and try again.'
      });
      console.error('Waitlist submission error:', error);
    }
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // In a real implementation, you would send the subscription data to your backend
    console.log('Subscription submitted:', { 
      ...formData, 
      plan: selectedPlan, 
      size: selectedSize 
    });
    
    // Show success message
    setSubscribeSuccess(true);
    
    // Reset after 5 seconds
    setTimeout(() => {
      setSubscribeSuccess(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        deliveryNotes: ''
      });
    }, 5000);
  };
  
  // Box content by season
  const seasonalContents = {
    spring: [
      'Fresh Spring Greens',
      'Early Strawberries',
      'Asparagus',
      'Green Onions',
      'Radishes',
      'Farm Fresh Eggs',
      'Artisan Bread'
    ],
    summer: [
      'Vine-Ripe Tomatoes',
      'Sweet Corn',
      'Bell Peppers',
      'Fresh Cucumber',
      'Summer Squash',
      'Garden Beets',
      'Seasonal Cantaloupe'
    ],
    fall: [
      'Crisp Apples',
      'Sweet Potatoes',
      'Butternut Squash',
      'Fresh Kale',
      'Storage Onions',
      'Apple Cider',
      'Homemade Preserves'
    ],
    winter: [
      'Winter Squash',
      'Root Vegetables',
      'Greenhouse Greens',
      'Stored Apples',
      'Preserves',
      'Farm Fresh Eggs',
      'Baked Goods'
    ]
  };
  
  // Updated box dimensions and pricing based on actual produce costs
  const boxSizes = {
    small: {
      title: 'Small Box',
      serving: 'Perfect for 1-2 people',
      items: '6-8 seasonal items',
      description: 'A thoughtful selection of our freshest picks',
      price: { weekly: '$15.99', biweekly: '$17.99', monthly: '$18.99' },
      savings: 'Save 15% vs. individual pricing',
      retailValue: '$18-22'
    },
    medium: {
      title: 'Medium Box',
      serving: 'Ideal for 2-4 people',
      items: '10-12 seasonal items',
      description: 'Our most popular size with great variety',
      price: { weekly: '$24.99', biweekly: '$27.99', monthly: '$28.99' },
      savings: 'Save 18% vs. individual pricing',
      retailValue: '$30-35'
    },
    large: {
      title: 'Large Box',
      serving: 'Great for 4-6 people',
      items: '14-16 seasonal items',
      description: 'Maximum variety and value for larger households',
      price: { weekly: '$37.99', biweekly: '$40.99', monthly: '$42.99' },
      savings: 'Save 20% vs. individual pricing',
      retailValue: '$45-52'
    }
  };
  
  // Current season - would normally be calculated programmatically
  const currentSeason = 'summer';
  
  // Calculate price based on selected options
  const calculatePrice = () => {
    return boxSizes[selectedSize].price[selectedPlan];
  };
  
  // Get retail value for transparency
  const getRetailValue = () => {
    return boxSizes[selectedSize].retailValue;
  };
  
  // Testimonials with real names and specific details (Ogilvy principle)
  const testimonials = [
    {
      quote: "The Buck's Box changed how our family eats. We've discovered vegetables we never tried before, and my kids actually get excited about what's coming each week!",
      author: "Jessica Anderson, Mother of 3, Customer since 2020"
    },
    {
      quote: "As a busy professional, I don't have time to shop, but I want quality produce. Buck's Box is the perfect solution - farm-fresh food delivered to my door without the hassle.",
      author: "Michael Torres, Software Engineer, Customer since 2021"
    },
    {
      quote: "I love the connection to seasonal eating. Opening my Buck's Box feels like unwrapping a gift each week, and the quality far exceeds anything at the grocery store.",
      author: "Emma Richardson, Local Teacher, Customer since 2019"
    }
  ];

  return (
    <div className="bucks-box">
      {/* Hero Section */}
      <section className={`box-hero ${isVisible ? 'visible' : ''}`}>
        <div className="box-hero-content">
          <h1>The Buck's Box</h1>
          <p className="hero-tag">Farm Fresh, Direct to Your Door</p>
          <p className="hero-subtitle">A weekly curated box of the season's finest produce, harvested at peak freshness and delivered straight to you.</p>
          <a href="#subscribe" className="cta-primary">Learn More</a>
        </div>
        <div className="box-hero-image">
          <img src={bucksBoxFrontImage} alt="Buck's Box filled with fresh produce" />
        </div>
      </section>
      
      {/* Box Benefits Section */}
      <section className="benefits-section">
        {/* Title container - separate from the benefits grid */}
        <div style={{ 
          width: '100%', 
          textAlign: 'center', 
          marginBottom: '3rem',
          display: 'block',
          maxWidth: '1280px',
          margin: '0 auto 3rem auto'
        }}>
          <h2>Why Choose The Buck's Box?</h2>
        </div>
        
        {/* Benefits Grid Container */}
        <div className="benefits-container" style={{ marginTop: '2rem' }}>
          {/* Override the grid layout to force 4 columns in a single row */}
          <div className="benefits-grid" style={{ 
            display: 'flex', 
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
            gap: '2rem'
          }}>
            <div className="benefit-card" style={{ flex: '1 1 0' }}>
              <div className="benefit-icon">🌿</div>
              <h3>Farm Fresh Produce</h3>
              <p>Harvested at peak ripeness for maximum flavor and nutritional value, often delivered within hours of picking.</p>
            </div>
            
            <div className="benefit-card" style={{ flex: '1 1 0' }}>
              <div className="benefit-icon">🚚</div>
              <h3>Free Home Delivery</h3>
              <p>Convenient weekly, bi-weekly, or monthly delivery right to your doorstep at no additional cost.</p>
            </div>
            
            <div className="benefit-card" style={{ flex: '1 1 0' }}>
              <div className="benefit-icon">💰</div>
              <h3>Great Value</h3>
              <p>Save 15-20% compared to purchasing the same items individually at our farmstand.</p>
            </div>
            
            <div className="benefit-card" style={{ flex: '1 1 0' }}>
              <div className="benefit-icon">🌎</div>
              <h3>Support Local Farming</h3>
              <p>Your subscription directly supports sustainable farming practices and our local farmstand.</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section className="how-it-works">
        <div className="how-container">
          <h2>How Buck's Box Works</h2>
          
          <div className="steps">
            <div className="step-item">
              <div className="step-number">1</div>
              <h3>Choose Your Box</h3>
              <p>Select the size and frequency that works best for your household.</p>
            </div>
            
            <div className="step-item">
              <div className="step-number">2</div>
              <h3>We Harvest & Pack</h3>
              <p>Our team carefully selects the freshest seasonal produce for your box.</p>
            </div>
            
            <div className="step-item">
              <div className="step-number">3</div>
              <h3>Delivery Day</h3>
              <p>Your box arrives at your doorstep on your scheduled delivery day.</p>
            </div>
            
            <div className="step-item">
              <div className="step-number">4</div>
              <h3>Enjoy!</h3>
              <p>Cook, share, and enjoy the farm-fresh taste of each season.</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* What's In The Box Section */}
      <section className="box-contents">
        <div className="contents-container">
          <h2>What's In The Box</h2>
          <p className="contents-intro">Each Buck's Box is thoughtfully curated with the season's best offerings. Here's a preview of what you might find in your {currentSeason} box:</p>
          
          <div className="season-contents">
            <div className="season-image">
              <img src={bucksBoxImage} alt={`${currentSeason} produce selection`} />
            </div>
            
            <div className="contents-list">
              <h3>Current {currentSeason.charAt(0).toUpperCase() + currentSeason.slice(1)} Selection</h3>
              <p className="season-note">While each box varies week to week, here's what you might expect this season:</p>
              
              <ul className="produce-list">
                {seasonalContents[currentSeason].map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              
              <p className="freshness-guarantee">
                <span className="guarantee-badge">FRESHNESS GUARANTEE</span>
                We stand behind the quality of every item in your box. Not satisfied? Let us know and we'll make it right.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Subscription Plans Section */}
      <section className="subscription-plans" id="subscribe">
        <div className="plans-container">
          <h2>Choose Your Buck's Box Plan</h2>
          <p className="plans-intro">Customize your subscription to fit your lifestyle and save money on farm-fresh produce.</p>
          
          <div className="plan-options">
            <div className="plan-selectors">
              <h3>1. Select Your Delivery Schedule</h3>
              <div className="frequency-selector">
                <button 
                  className={`frequency-btn ${selectedPlan === 'weekly' ? 'active' : ''}`}
                  onClick={() => handlePlanSelect('weekly')}
                >
                  Weekly
                </button>
                <button 
                  className={`frequency-btn ${selectedPlan === 'biweekly' ? 'active' : ''}`}
                  onClick={() => handlePlanSelect('biweekly')}
                >
                  Bi-Weekly
                </button>
                <button 
                  className={`frequency-btn ${selectedPlan === 'monthly' ? 'active' : ''}`}
                  onClick={() => handlePlanSelect('monthly')}
                >
                  Monthly
                </button>
              </div>
              
              <h3>2. Choose Your Box Size</h3>
              <div className="size-selector">
                {Object.keys(boxSizes).map((size) => (
                  <div 
                    key={size}
                    className={`size-option ${selectedSize === size ? 'active' : ''}`}
                    onClick={() => handleSizeSelect(size)}
                  >
                    <h4>{boxSizes[size].title}</h4>
                    <div className="size-details">
                      <p>{boxSizes[size].serving}</p>
                      <p>{boxSizes[size].items}</p>
                      <p className="size-description">{boxSizes[size].description}</p>
                      <p className="size-price">{boxSizes[size].price[selectedPlan]}</p>
                      <p className="size-savings">{boxSizes[size].savings}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="order-summary">
              <div className="summary-card">
                <h3>Your Buck's Box</h3>
                
                <div className="summary-details">
                  <div className="summary-item">
                    <span>Box Size:</span>
                    <span>{boxSizes[selectedSize].title}</span>
                  </div>
                  <div className="summary-item">
                    <span>Delivery:</span>
                    <span>{selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)}</span>
                  </div>
                  <div className="summary-item">
                    <span>Contents:</span>
                    <span>{boxSizes[selectedSize].items}</span>
                  </div>
                  <div className="summary-item">
                    <span>Retail Value:</span>
                    <span>{getRetailValue()}</span>
                  </div>
                  <div className="summary-price">
                    <span>Your Price:</span>
                    <span>{calculatePrice()}</span>
                  </div>
                </div>
                
                <div className="summary-perks">
                  <div className="perk-item">✓ Free Delivery</div>
                  <div className="perk-item">✓ No Commitment - Cancel Anytime</div>
                  <div className="perk-item">✓ Freshness Guarantee</div>
                  <div className="perk-item">✓ {boxSizes[selectedSize].savings}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Coming Soon Subscription Section - UPDATED WITH API INTEGRATION */}
      <section className="subscription-form">
        <div className="form-container coming-soon-container">
          <div className="coming-soon-icon">
            <Sprout size={48} color="#a67c52" />
            <Calendar size={48} color="#a67c52" />
          </div>
          <h2>Coming Mid-June 2025</h2>
          <p className="coming-soon-message">
            We're busy cultivating the perfect Buck's Box experience for you. Our subscription service will be sprouting soon, bringing farm-fresh goodness directly to your doorstep beginning in mid-June 2025.
          </p>
          <div className="coming-soon-details">
            <p>While we prepare to launch, we invite you to join our waiting list. Be among the first to experience the Buck's Box when we harvest our inaugural delivery.</p>
            
            {/* Waitlist status message */}
            {(waitlistStatus.success || waitlistStatus.error) && (
              <div className={`waitlist-status-message ${waitlistStatus.success ? 'success' : 'error'}`} style={{
                padding: '10px',
                marginBottom: '15px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: waitlistStatus.success ? '#e8f5e9' : '#ffebee',
                color: waitlistStatus.success ? '#2e7d32' : '#c62828'
              }}>
                {waitlistStatus.success ? 
                  <CheckCircle size={20} style={{ marginRight: '10px' }} /> : 
                  <AlertCircle size={20} style={{ marginRight: '10px' }} />
                }
                {waitlistStatus.message}
              </div>
            )}
            
            <div className="email-notify-container">
              <input 
                type="email" 
                placeholder="Your email address" 
                className="notify-email-input"
                value={waitlistEmail}
                onChange={handleWaitlistEmailChange}
                disabled={waitlistStatus.loading}
              />
              <button 
                className="notify-btn"
                onClick={handleWaitlistSubmit}
                disabled={waitlistStatus.loading}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  opacity: waitlistStatus.loading ? 0.8 : 1
                }}
              >
                {waitlistStatus.loading ? (
                  <>
                    <Loader size={16} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} />
                    Processing...
                  </>
                ) : 'Notify Me'}
              </button>
            </div>
            <p className="notify-note">We'll let you know the moment subscriptions open. No spam, just fresh updates.</p>
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="testimonials-container">
          <h2>What Our Customers Say</h2>
          
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div className="testimonial-card" key={index}>
                <div className="quote-mark">"</div>
                <p className="testimonial-quote">{testimonial.quote}</p>
                <div className="testimonial-author">— {testimonial.author}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="faq-section">
        <div className="faq-container">
          <h2>Frequently Asked Questions</h2>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How do I customize my box?</h3>
              <p>While our Buck's Box is curated based on seasonal availability, you can note any preferences or allergies in your delivery notes. We'll do our best to accommodate your needs.</p>
            </div>
            
            <div className="faq-item">
              <h3>What if I'm going on vacation?</h3>
              <p>You can easily pause your subscription at any time by calling or contacting our customer support at least 48 hours before your scheduled delivery.</p>
            </div>
            
            <div className="faq-item">
              <h3>What's your delivery area?</h3>
              <p>We currently deliver to all addresses within a 20-mile radius of our farmstand in Alsip, IL. Enter your address at checkout to confirm availability.</p>
            </div>
            
            <div className="faq-item">
              <h3>How are the boxes packed?</h3>
              <p>We pack all produce in eco-friendly, insulated bags that keep your items fresh. All packaging is either compostable or recyclable.</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CSS for animation */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

export default BucksBox;